import { useState } from 'react';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

const features = [
  {
    icon: 'ri-rfid-line',
    title: 'Embedded RFID Tags',
    description: 'Each tire is fitted with a durable RFID chip embedded directly into the rubber, surviving the full lifecycle of the tire.'
  },
  {
    icon: 'ri-database-2-line',
    title: 'Full Lifecycle Tracking',
    description: 'From manufacture to disposal, every tire movement, fitment, and service event is recorded in a tamper-proof digital ledger.'
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Fleet Intelligence',
    description: 'Real-time dashboards give fleet operators instant visibility into tire health, mileage, pressure history, and compliance status.'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Counterfeit Prevention',
    description: 'Unique cryptographic identifiers make it impossible to substitute or misrepresent tires, protecting brands and end users alike.'
  },
  {
    icon: 'ri-recycle-line',
    title: 'Sustainability Reporting',
    description: 'Automated ESG reporting tracks retreading rates, carbon footprint, and end-of-life disposal compliance across entire fleets.'
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud-Native Platform',
    description: 'A SaaS platform accessible from any device, integrating with existing fleet management, ERP, and telematics systems via open APIs.'
  }
];

const stats = [
  { value: '100M+', label: 'Tires Trackable' },
  { value: '98.3%', label: 'Read Accuracy' },
  { value: '10yr+', label: 'Tag Lifespan' },
  { value: '360°', label: 'Lifecycle Visibility' }
];

const useCases = [
  {
    icon: 'ri-car-line',
    title: 'Automotive Manufacturing',
    description: 'OEMs and tire manufacturers embed RFID tags during production, enabling warranty tracking, recall management, and quality assurance throughout the supply chain.',
    benefits: ['Automated inventory management', 'Warranty fraud prevention', 'Recall traceability']
  },
  {
    icon: 'ri-truck-line',
    title: 'Logistics & Fleet Operations',
    description: 'Commercial fleet operators gain real-time visibility into tire performance, maintenance schedules, and replacement cycles across thousands of vehicles.',
    benefits: ['Predictive maintenance alerts', 'Fuel efficiency optimization', 'Downtime reduction']
  },
  {
    icon: 'ri-building-line',
    title: 'Heavy Industry & Mining',
    description: 'Mining, construction, and agriculture sectors track high-value OTR tires, ensuring optimal usage, retreading opportunities, and compliance with safety regulations.',
    benefits: ['Asset utilization tracking', 'Cost per hour analysis', 'Safety compliance reporting']
  }
];

const howItWorks = [
  {
    step: '01',
    title: 'Tag Embedding',
    description: 'RFID chips are embedded into tires during the manufacturing process, becoming a permanent part of the tire structure.',
    icon: 'ri-cpu-line'
  },
  {
    step: '02',
    title: 'Data Capture',
    description: 'Fixed and handheld RFID readers capture tire data at every touchpoint — from warehouse to vehicle fitment to service bay.',
    icon: 'ri-scan-line'
  },
  {
    step: '03',
    title: 'Cloud Processing',
    description: 'All tire events are transmitted to the DIIVY cloud platform, where AI algorithms analyze patterns and generate actionable insights.',
    icon: 'ri-cloud-line'
  },
  {
    step: '04',
    title: 'Intelligence Delivery',
    description: 'Stakeholders access real-time dashboards, automated alerts, and compliance reports via web and mobile applications.',
    icon: 'ri-dashboard-line'
  }
];

const integrations = [
  { name: 'Fleet Management Systems', icon: 'ri-route-line' },
  { name: 'ERP Platforms', icon: 'ri-organization-chart' },
  { name: 'Telematics Solutions', icon: 'ri-signal-tower-line' },
  { name: 'Maintenance Software', icon: 'ri-tools-line' },
  { name: 'ESG Reporting Tools', icon: 'ri-leaf-line' },
  { name: 'Supply Chain Systems', icon: 'ri-links-line' }
];

export default function TireTechnology() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Tire RFID Technology',
        item: `${siteUrl}/tire-technology`
      }
    ]
  };

  const tireTechSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Tire RFID Technology | DIIVY Lifecycle Management Platform',
    url: `${siteUrl}/tire-technology`,
    description: 'DIIVY\'s tire RFID technology platform enables full lifecycle tracking, fleet intelligence, and sustainability compliance from manufacture to disposal.',
    about: {
      '@type': 'SoftwareApplication',
      name: 'DIIVY Tire RFID Platform',
      applicationCategory: 'BusinessApplication',
      description: 'Cloud-native SaaS platform for tire lifecycle management using embedded RFID technology',
      featureList: [
        'Embedded RFID Tags',
        'Full Lifecycle Tracking',
        'Fleet Intelligence',
        'Counterfeit Prevention',
        'Sustainability Reporting',
        'Cloud-Native Platform'
      ],
      offers: {
        '@type': 'Offer',
        description: 'Enterprise tire lifecycle management platform'
      }
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'Diivy Medical Technology',
      url: siteUrl
    }
  };

  return (
    <>
      <SEOHead
        title="Tire RFID Technology | DIIVY Lifecycle Management Platform | Diivy"
        description="DIIVY's tire RFID technology platform enables full lifecycle tracking, fleet intelligence, and sustainability compliance from manufacture to disposal. Embedded RFID tags provide 360° visibility for automotive, logistics, and heavy industry sectors."
        keywords="tire RFID, tire tracking, fleet management, RFID technology, tire lifecycle, sustainability reporting, automotive technology, logistics solutions"
        canonical="/tire-technology"
        ogTitle="Tire RFID Technology | DIIVY Lifecycle Management Platform"
        ogDescription="Full lifecycle tire tracking with embedded RFID — fleet intelligence, counterfeit prevention, and sustainability compliance for automotive, logistics, and heavy industry."
        ogUrl="/tire-technology"
        twitterTitle="Tire RFID Technology | DIIVY Lifecycle Management Platform"
        twitterDescription="Full lifecycle tire tracking with embedded RFID — fleet intelligence and sustainability compliance."
      />
      <JsonLd data={tireTechSchema} />
      <JsonLd data={breadcrumbSchema} />
      
      <Navigation />

      {/* Hero Banner */}
      <div className="relative pt-[88px] pb-0 bg-[#0A0F1E] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, #C9A96E 40px, #C9A96E 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, #C9A96E 40px, #C9A96E 41px)'
          }}
        ></div>

        <div className="relative max-w-[1200px] mx-auto px-12 pt-20 pb-16">
          {/* Top: Text left, Video right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-10">
              <div className="flex items-center gap-5">
                <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
                <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                  Portfolio Company
                </p>
              </div>

              <div className="space-y-4">
                <h1 className="text-5xl font-light text-white leading-[1.15] tracking-[-0.02em]">
                  Tire Intelligence<br />
                  <span className="font-semibold">Powered by RFID</span>
                </h1>
                <div className="flex items-center gap-3 pt-2">
                  <span className="text-[22px] font-bold tracking-widest text-[#C9A96E]">D//VY</span>
                  <span className="text-white/30 text-sm">|</span>
                  <span className="text-white/50 text-[13px] tracking-wide">diivy.com</span>
                </div>
              </div>

              <p className="text-[16px] leading-[1.9] text-white/60 font-light max-w-[480px]">
                DIIVY is a pioneering tire lifecycle management platform that embeds RFID technology directly into tires — enabling full traceability, fleet intelligence, and sustainability compliance from manufacture to disposal.
              </p>

              <p className="text-[15px] leading-[1.8] text-white/50 font-light max-w-[480px]">
                Operating across automotive, logistics, and heavy industry sectors, DIIVY transforms tires from a commodity into a connected, data-rich asset — reducing costs, improving safety, and delivering measurable ESG outcomes.
              </p>

              <a
                href="https://www.diivy.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-3.5 border border-[#C9A96E]/40 text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] hover:border-[#C9A96E] transition-all duration-300 whitespace-nowrap tracking-wide"
              >
                Visit DIIVY
                <i className="ri-external-link-line text-sm"></i>
              </a>
            </div>

            {/* Right — Video */}
            <div className="rounded-sm overflow-hidden border border-white/10">
              <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                <iframe
                  src="https://www.youtube.com/embed/3NezmOWCRL8?si=PXxEfmd8TfYVzpeJ"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="DIIVY Tire RFID Platform"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Bottom: Stats row */}
          <div className="grid grid-cols-4 gap-6 mt-16 pt-16 border-t border-white/5">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="text-center py-6 border border-white/5 rounded-sm bg-white/[0.03] hover:border-[#C9A96E]/30 hover:bg-white/[0.05] transition-all duration-500"
              >
                <p className="text-4xl font-bold text-[#C9A96E] tracking-[-0.02em]">{stat.value}</p>
                <p className="mt-2 text-[12px] font-medium text-white/40 tracking-[0.12em] uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-28 bg-[#F7F4EF]">
        <div className="max-w-[1200px] mx-auto px-12">
          <div className="mb-20 text-center space-y-6">
            <div className="flex items-center justify-center gap-5">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Platform Capabilities
              </p>
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
            </div>
            <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[600px] mx-auto">
              End-to-End Tire<br />
              <span className="font-semibold">Lifecycle Management</span>
            </h2>
            <p className="text-[15px] leading-[1.8] text-gray-500 font-light max-w-[560px] mx-auto">
              DIIVY's platform covers every stage of a tire's life — giving manufacturers, fleet operators, and regulators a single source of truth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group p-8 rounded-sm border cursor-pointer transition-all duration-400 ${
                  activeFeature === index
                    ? 'border-[#C9A96E]/50 bg-white shadow-sm'
                    : 'border-[#0A0F1E]/8 bg-white/60 hover:border-[#C9A96E]/30 hover:bg-white hover:shadow-sm'
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className="space-y-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-sm bg-[#C9A96E]/10 group-hover:bg-[#C9A96E]/20 transition-colors duration-300">
                    <i className={`${feature.icon} text-xl text-[#C9A96E]`}></i>
                  </div>
                  <h3 className="text-[16px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                    {feature.title}
                  </h3>
                  <p className="text-[14px] leading-[1.75] text-gray-500 font-light">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-12">
          <div className="mb-20 text-center space-y-6">
            <div className="flex items-center justify-center gap-5">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Industry Applications
              </p>
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
            </div>
            <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[600px] mx-auto">
              Transforming Tire Management<br />
              <span className="font-semibold">Across Industries</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className={`p-10 rounded-sm border cursor-pointer transition-all duration-400 ${
                  activeUseCase === index
                    ? 'border-[#C9A96E]/50 bg-[#F7F4EF] shadow-sm'
                    : 'border-[#0A0F1E]/8 bg-white hover:border-[#C9A96E]/30 hover:bg-[#F7F4EF]/50'
                }`}
                onClick={() => setActiveUseCase(index)}
              >
                <div className="space-y-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-sm bg-[#C9A96E]/10">
                    <i className={`${useCase.icon} text-2xl text-[#C9A96E]`}></i>
                  </div>
                  <h3 className="text-[18px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                    {useCase.title}
                  </h3>
                  <p className="text-[14px] leading-[1.8] text-gray-500 font-light">
                    {useCase.description}
                  </p>
                  <div className="pt-4 space-y-3">
                    {useCase.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <i className="ri-check-line text-[#C9A96E] text-sm mt-0.5"></i>
                        <span className="text-[13px] text-gray-600 font-light">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-28 bg-[#F7F4EF]">
        <div className="max-w-[1200px] mx-auto px-12">
          <div className="mb-20 text-center space-y-6">
            <div className="flex items-center justify-center gap-5">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Technology Process
              </p>
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
            </div>
            <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[600px] mx-auto">
              How DIIVY<br />
              <span className="font-semibold">Technology Works</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="p-8 rounded-sm border border-[#0A0F1E]/8 bg-white hover:border-[#C9A96E]/30 hover:shadow-sm transition-all duration-400">
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <span className="text-5xl font-bold text-[#C9A96E]/20 tracking-[-0.02em]">
                        {item.step}
                      </span>
                      <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[#C9A96E]/10">
                        <i className={`${item.icon} text-lg text-[#C9A96E]`}></i>
                      </div>
                    </div>
                    <h3 className="text-[16px] font-semibold text-[#0A0F1E] tracking-[-0.01em]">
                      {item.title}
                    </h3>
                    <p className="text-[13px] leading-[1.75] text-gray-500 font-light">
                      {item.description}
                    </p>
                  </div>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-[1px] bg-[#C9A96E]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Platform Integrations */}
      <div className="py-28 bg-white">
        <div className="max-w-[1200px] mx-auto px-12">
          <div className="mb-20 text-center space-y-6">
            <div className="flex items-center justify-center gap-5">
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Seamless Integration
              </p>
              <span className="block w-10 h-[1px] bg-[#C9A96E]"></span>
            </div>
            <h2 className="text-4xl font-light text-[#0A0F1E] leading-[1.2] tracking-[-0.02em] max-w-[600px] mx-auto">
              Connect with Your<br />
              <span className="font-semibold">Existing Systems</span>
            </h2>
            <p className="text-[15px] leading-[1.8] text-gray-500 font-light max-w-[560px] mx-auto">
              DIIVY integrates seamlessly with your current technology stack through open APIs and pre-built connectors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="p-6 rounded-sm border border-[#0A0F1E]/8 bg-[#F7F4EF]/50 hover:border-[#C9A96E]/30 hover:bg-[#F7F4EF] transition-all duration-400 text-center"
              >
                <div className="space-y-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-white mx-auto">
                    <i className={`${integration.icon} text-xl text-[#C9A96E]`}></i>
                  </div>
                  <p className="text-[12px] font-medium text-[#0A0F1E] leading-[1.4]">
                    {integration.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-28 bg-[#0A0F1E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, #C9A96E 40px, #C9A96E 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, #C9A96E 40px, #C9A96E 41px)'
          }}
        ></div>

        <div className="relative max-w-[800px] mx-auto px-12 text-center space-y-10">
          <div className="space-y-6">
            <h2 className="text-4xl font-light text-white leading-[1.2] tracking-[-0.02em]">
              Ready to Transform Your<br />
              <span className="font-semibold">Tire Management?</span>
            </h2>
            <p className="text-[15px] leading-[1.8] text-white/60 font-light max-w-[560px] mx-auto">
              Discover how DIIVY's RFID platform can reduce costs, improve safety, and deliver measurable sustainability outcomes for your organization.
            </p>
          </div>

          <div className="flex items-center justify-center gap-6">
            <a
              href="https://www.diivy.com"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#C9A96E] text-[13px] font-semibold text-[#0A0F1E] rounded-sm hover:bg-[#C9A96E]/90 transition-all duration-300 whitespace-nowrap tracking-wide"
            >
              Visit DIIVY Platform
              <i className="ri-external-link-line text-sm"></i>
            </a>
            <a
              href="/contact"
              className="inline-flex items-center gap-3 px-8 py-3.5 border border-[#C9A96E]/40 text-[13px] font-semibold text-[#C9A96E] rounded-sm hover:bg-[#C9A96E] hover:text-[#0A0F1E] hover:border-[#C9A96E] transition-all duration-300 whitespace-nowrap tracking-wide"
            >
              Contact Us
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}